'use client';
import { useEffect, useRef } from 'react';
import useSWR from 'swr';
import { authHeaders } from '@/lib/backtest/auth';
import {
  NOT_OPENED_ACCOUNT,
  type LiveAccount,
  type LiveBalance,
  type LivePosition,
  type LiveRun,
} from './types';

// 全局 SWR fetcher（app-context apiMiddleware）：key 'api:/x' → 请求 /api/x 并自动带 Authorization。

const accountInProgress = (s?: LiveAccount['status']) =>
  s === 'pending_init' || s === 'initializing';

export function useLiveAccount() {
  const { data: apiAccessToken } = useSWR('apiAccessToken');

  const { data, error, isLoading, mutate } = useSWR<LiveAccount>(
    apiAccessToken ? 'api:/live-trading/account' : null,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false, // 404=未开通，不重试刷屏
      // 开通中轮询，active/failed 后停（404 时 d=undefined → 不轮询）。
      refreshInterval: (d) => (accountInProgress(d?.status) ? 3_000 : 0),
    },
  );
  // 关键：account 只在「加载中」才是 undefined；任何已 settle 的状态（含 404/报错）
  // 都回落到 NOT_OPENED，避免整块连同开通入口被隐藏。
  const account = data ?? (isLoading ? undefined : NOT_OPENED_ACCOUNT);
  return { account, isLoading, error, mutate };
}

// 进入页面即确保实盘账户：account 落到 pending_init（未开通）时自动调 init（幂等、异步）。
// 仅 pending_init 触发，不自动重试 failed（避免循环）；单次尝试后交给 useLiveAccount 轮询推进到 active。
// ref 守卫防 re-render / StrictMode 重复 POST；重新进入页面（组件重挂）会用新 ref 再试一次。
export function useEnsureLiveAccount() {
  const { account, mutate } = useLiveAccount();
  const attempted = useRef(false);
  useEffect(() => {
    if (attempted.current) return;
    if (account?.status !== 'pending_init') return;
    attempted.current = true;
    fetch('/api/live-trading/account/init', { method: 'POST', headers: authHeaders() })
      .then(() => mutate())
      .catch(() => {});
  }, [account?.status, mutate]);
}

export function useLiveBalance() {
  const { data, isLoading, mutate } = useSWR<LiveBalance>('api:/live-trading/balance', {
    revalidateOnFocus: false,
    refreshInterval: 15_000,
  });
  return { balance: data, isLoading, mutate };
}

export function useLiveRuns() {
  const { data, isLoading, mutate } = useSWR<{ runs: LiveRun[] }>('api:/live-trading/runs', {
    revalidateOnFocus: false,
    // 有 active run 时 5s 轮询，否则停。
    refreshInterval: (d) => ((d?.runs ?? []).some((r) => r.status === 'active') ? 5_000 : 0),
  });
  return { runs: data?.runs ?? [], isLoading, mutate };
}

export function useLivePositions() {
  const { data, isLoading, mutate } = useSWR<LivePosition[]>('api:/live-trading/positions', {
    revalidateOnFocus: false,
    refreshInterval: 15_000,
  });
  return { positions: data ?? [], isLoading, mutate };
}
